import { without } from 'lodash';

const TEMPORAL_BINNING_TOKENS = {
  YEAR: 'year',
  QUARTER: 'quarter',
  MONTH: 'month',
  DATE: 'date',
  DAY: 'day',
  HOURS: 'hours',
  MINUTES: 'minutes',
  SECONDS: 'seconds',
  MILLISECONDS: 'milliseconds',
};

const TEMPORAL_BINNING_TOKEN_ORDER = [
  TEMPORAL_BINNING_TOKENS.YEAR,
  TEMPORAL_BINNING_TOKENS.QUARTER,
  TEMPORAL_BINNING_TOKENS.MONTH,
  TEMPORAL_BINNING_TOKENS.DATE,
  TEMPORAL_BINNING_TOKENS.DAY,
  TEMPORAL_BINNING_TOKENS.HOURS,
  TEMPORAL_BINNING_TOKENS.MINUTES,
  TEMPORAL_BINNING_TOKENS.SECONDS,
  TEMPORAL_BINNING_TOKENS.MILLISECONDS,
];

const TEMPORAL_BINNING_NON_PERIODIC_TOKENS = [
  TEMPORAL_BINNING_TOKENS.QUARTER,
  TEMPORAL_BINNING_TOKENS.DAY,
];

/**
 * Used when we're turning a (isPeriodic, temporalBinningToken) tuple into an
 * array of temporal tokens. We use this array to determine what slice of the tokens
 * is required for the appropriate binning.
 */
const TEMPORAL_BINNING_PERIODIC_TOKEN_ORDER = without(
  TEMPORAL_BINNING_TOKEN_ORDER,
  ...TEMPORAL_BINNING_NON_PERIODIC_TOKENS
);

/**
 * This object maps moment.js format strings to an array of tokens. While we
 * need the mapping in the opposite direction (tokens to format strings), it's
 * not possible to use arrays as object keys. We use lodash's findKey() to
 * search for the correct format given a token array. The performance impact
 * is negligible given the small size of the object.
 */
const TEMPORAL_BINNING_FORMAT_TO_TOKENS_MAP = {
  // year, non-periodic
  YYYY: [TEMPORAL_BINNING_TOKENS.YEAR],
  // month, non-periodic
  'MMM YYYY': [TEMPORAL_BINNING_TOKENS.YEAR, TEMPORAL_BINNING_TOKENS.MONTH],
  // date of the month, non-periodic
  'MMM D, YYYY': [
    TEMPORAL_BINNING_TOKENS.YEAR,
    TEMPORAL_BINNING_TOKENS.MONTH,
    TEMPORAL_BINNING_TOKENS.DATE,
  ],
  // hours, non-periodic
  'MMM D, YYYY HH:00': [
    TEMPORAL_BINNING_TOKENS.YEAR,
    TEMPORAL_BINNING_TOKENS.MONTH,
    TEMPORAL_BINNING_TOKENS.DATE,
    TEMPORAL_BINNING_TOKENS.HOURS,
  ],
  // minutes, non-periodic
  'MMM D, YYYY HH:mm': [
    TEMPORAL_BINNING_TOKENS.YEAR,
    TEMPORAL_BINNING_TOKENS.MONTH,
    TEMPORAL_BINNING_TOKENS.DATE,
    TEMPORAL_BINNING_TOKENS.HOURS,
    TEMPORAL_BINNING_TOKENS.MINUTES,
  ],
  // seconds, non-periodic
  'MMM D, YYYY HH:mm:ss': [
    TEMPORAL_BINNING_TOKENS.YEAR,
    TEMPORAL_BINNING_TOKENS.MONTH,
    TEMPORAL_BINNING_TOKENS.DATE,
    TEMPORAL_BINNING_TOKENS.HOURS,
    TEMPORAL_BINNING_TOKENS.MINUTES,
    TEMPORAL_BINNING_TOKENS.SECONDS,
  ],
  // month, periodic
  MMM: [TEMPORAL_BINNING_TOKENS.MONTH],
  // date of the month, periodic
  D: [TEMPORAL_BINNING_TOKENS.DATE],
  // day of the week, periodic
  dddd: [TEMPORAL_BINNING_TOKENS.DAY],
  // hours, periodic
  'HH:00': [TEMPORAL_BINNING_TOKENS.HOURS],
  // minutes, periodic
  mm: [TEMPORAL_BINNING_TOKENS.MINUTES],
  // seconds, periodic
  ss: [TEMPORAL_BINNING_TOKENS.SECONDS],
};

const TEMPORAL_BINNING_AGG_FACTORY = {
  [TEMPORAL_BINNING_TOKENS.YEAR]: (field, timezone) => ({
    $year: timezone ? { date: `$${field}`, timezone } : `$${field}`,
  }),
  [TEMPORAL_BINNING_TOKENS.QUARTER]: (field, timezone, startMonth = 1) => ({
    $sum: [
      // add 1 to make quarter 1-based (Q1, Q2, ...)
      {
        $abs: {
          // take absolute value
          $mod: [
            // mod 4 in case of negative result
            {
              $trunc: {
                // truncate decimals
                $divide: [
                  // divide by 3 to get quarter
                  {
                    $sum: [
                      {
                        $month: timezone
                          ? { date: `$${field}`, timezone }
                          : `$${field}`,
                      },
                      -startMonth,
                    ],
                  }, // now 0-based month
                  3,
                ],
              },
            },
            4,
          ],
        },
      },
      1,
    ],
  }),
  // months in the aggregation pipeline are 1-based but in javascript it is 0-based
  [TEMPORAL_BINNING_TOKENS.MONTH]: (field, timezone) => ({
    $subtract: [
      { $month: timezone ? { date: `$${field}`, timezone } : `$${field}` },
      1,
    ],
  }),
  [TEMPORAL_BINNING_TOKENS.DATE]: (field, timezone) => ({
    $dayOfMonth: timezone ? { date: `$${field}`, timezone } : `$${field}`,
  }),
  // days in the aggregation pipeline are 1 (Sunday) but in moment.js it is 1 (Monday)
  [TEMPORAL_BINNING_TOKENS.DAY]: (field, timezone) => ({
    $add: [
      { $dayOfWeek: timezone ? { date: `$${field}`, timezone } : `$${field}` },
      6,
    ],
  }),
  [TEMPORAL_BINNING_TOKENS.HOURS]: (field, timezone) => ({
    $hour: timezone ? { date: `$${field}`, timezone } : `$${field}`,
  }),
  [TEMPORAL_BINNING_TOKENS.MINUTES]: (field, timezone) => ({
    $minute: timezone ? { date: `$${field}`, timezone } : `$${field}`,
  }),
  [TEMPORAL_BINNING_TOKENS.SECONDS]: (field, timezone) => ({
    $second: timezone ? { date: `$${field}`, timezone } : `$${field}`,
  }),
  [TEMPORAL_BINNING_TOKENS.MILLISECONDS]: (field, timezone) => ({
    $millisecond: timezone ? { date: `$${field}`, timezone } : `$${field}`,
  }),
};

const TEMPORAL_BINNING_UTC_TOKEN = 'utc';

const TEMPORAL_BINNING_LABELS = {
  [TEMPORAL_BINNING_TOKENS.YEAR]: 'Year',
  [TEMPORAL_BINNING_TOKENS.MONTH]: 'Month',
  [TEMPORAL_BINNING_TOKENS.DATE]: 'Date of the Month',
  [TEMPORAL_BINNING_TOKENS.DAY]: 'Day of the Week',
  [TEMPORAL_BINNING_TOKENS.HOURS]: 'Hour',
  [TEMPORAL_BINNING_TOKENS.MINUTES]: 'Minute',
  [TEMPORAL_BINNING_TOKENS.SECONDS]: 'Second',
};

const TEMPORAL_BINNING_OPTIONS = Object.keys(TEMPORAL_BINNING_LABELS).map(
  key => ({ value: key, label: TEMPORAL_BINNING_LABELS[key] })
);

// defaults periodic settings, null indicates no default selected
const TEMPORAL_BINNING_PERIODIC_DEFAULTS = {
  [TEMPORAL_BINNING_TOKENS.YEAR]: false,
  [TEMPORAL_BINNING_TOKENS.MONTH]: null,
  [TEMPORAL_BINNING_TOKENS.DATE]: null,
  [TEMPORAL_BINNING_TOKENS.DAY]: true,
  [TEMPORAL_BINNING_TOKENS.HOURS]: null,
  [TEMPORAL_BINNING_TOKENS.MINUTES]: null,
  [TEMPORAL_BINNING_TOKENS.SECONDS]: null,
};

const BINNING_ON = 'BINNING ON';
const BINNING_OFF = 'BINNING OFF';

const DEFAULT_QUANTITATIVE_BIN_SIZE = 10;

export {
  BINNING_OFF,
  BINNING_ON,
  DEFAULT_QUANTITATIVE_BIN_SIZE,
  TEMPORAL_BINNING_AGG_FACTORY,
  TEMPORAL_BINNING_FORMAT_TO_TOKENS_MAP,
  TEMPORAL_BINNING_LABELS,
  TEMPORAL_BINNING_OPTIONS,
  TEMPORAL_BINNING_PERIODIC_DEFAULTS,
  TEMPORAL_BINNING_TOKENS,
  TEMPORAL_BINNING_TOKEN_ORDER,
  TEMPORAL_BINNING_UTC_TOKEN,
  TEMPORAL_BINNING_NON_PERIODIC_TOKENS,
  TEMPORAL_BINNING_PERIODIC_TOKEN_ORDER,
};
